import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import useApi from '../../../utility/Hooks/Api/useApi';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import DefaultInput from '../../Inputs/Default/DefaultInput';
import PartsSpecificationsSelect from "../../Selects/PartSpecifications/PartsSpecificationsSelect";
import DefaultAddFormHeader from '../Helpers/FormHeaders/DefaultAddFormHeader';
import DefaultAddFormNavigation from "../Helpers/FormNavigation/DefaultAddFormNavigation";
import { PartsApi } from '../../../services/Api/PartsApi';
import DefaultTextArea from '../../Inputs/Default/DefaultTextArea';
import DefaultDatePicker from '../../Selects/DatePicker/DefaultDatePicker';
import { use } from 'react';


export default function PartsAddForm({ }) {
    const navigate = useNavigate();
    const { data, request, loading } = useApi();

    const [itemData, setItemData] = useState(
        {
            "serialNumber": null,
            "reference": "Ordered",
            "location": "Manufacturer",
            "orderNumber": null,
            "partSpecificationsId": null,
            "orderDate": "",
            "shippingDate": ""
        }
    );
    const [serialInputText, setSerialInputText] = useState('');
    const [serialNumbers, setSerialNumbers] = useState([]);
    const [faultySerialNumbers, setFaultySerialNumbers] = useState([]);
    const [originalSerialNumbers, setOriginalSerialNumbers] = useState([]);
    const [hasFaultySerials, setHasFaultySerials] = useState(false);
    const [error, setError] = useState(null);
    const [openTab, setOpenTab] = useState(1);

    const lastIndex = 4;

    const tabItems = [
        { title: "Product type", subtitle: "Part specifications" },
        { title: "Reference", subtitle: "Serial numbers of parts" },
        { title: "Order details", subtitle: "Details of ordered parts" },
    ];

    const serialNumberHandler = (e) => {
        const value = e.target.value;
        setSerialInputText(value); // keep textarea value in sync
    
        const newArr = value.split(/\n/).map(line => line.trim()).filter(Boolean);
        setSerialNumbers(newArr);
    };
    

    const orderNumberHandler = (e) => {
        setItemData({ ...itemData, orderNumber: e.target.value })
    };

    const supplierHandler = (e) => {
        setItemData({ ...itemData, supplier: e.target.value })
    };

    const partSpecificationsHandler = (e) => {
        setItemData({ ...itemData, partSpecificationsId: e.target.value })
    };

    const orderDateHandler = (e) => {
        setItemData({ ...itemData, orderDate: moment(e).format("MM-DD-yyyy").toString() })
    };

    const shippingDateHandler = (e) => {
        setItemData({ ...itemData, shippingDate: moment(e).format("MM-DD-yyyy").toString() })
    };

    const nextBtnHandler = (e) => {
        e.preventDefault();

        const positionLastIndex = openTab >= lastIndex && !loading;
        const nextCondition = openTab === (lastIndex - 1);
        const continueCondition = positionLastIndex && !error;
        const retryCondition = positionLastIndex && error;

        const createRequest = async (serialsToProcess) => {
            const faulty = [];
            const successful = [];

            for (const serialNumber of serialsToProcess) {
                const part = { ...itemData, serialNumber };

                try {
                    await request(PartsApi.createPart(part));
                    successful.push(serialNumber);
                } catch (err) {
                    setError(err);
                    if (err?.response?.data === "Serial number should be unique per PartSpecifictation") {
                        faulty.push(serialNumber);
                    }
                }
            }

            if (successful.length > 0) {
                // Remove successful serials immediately
                const updatedSerials = serialNumbers.filter(sn => !successful.includes(sn));
                setSerialNumbers(updatedSerials);
                setSerialInputText(updatedSerials.join("\n"));
            }

            if (faulty.length > 0) {
                setFaultySerialNumbers(faulty); // Save faulty ones for retry
                setHasFaultySerials(true);
                setOriginalSerialNumbers(serialNumbers); // Save the serialNumbers before retry
                setSerialInputText(faulty.join("\n"));
            } else {
                setHasFaultySerials(false);
                setFaultySerialNumbers([]);
                setOriginalSerialNumbers([]);
                setSerialInputText("");
            }
        };

        // Helper function to check if user changed the serials
        const areSerialNumbersSame = (arr1, arr2) => {
            if (arr1.length !== arr2.length) return false;
            const sorted1 = [...arr1].sort();
            const sorted2 = [...arr2].sort();
            return sorted1.every((value, index) => value === sorted2[index]);
        };

        if (nextCondition) {
            createRequest(serialNumbers); // first submit with all serials
            setOpenTab(lastIndex);
        } else if (continueCondition) {
            navigate("/main/parts");
        } else if (retryCondition) {
            console.log("retry");
            setError(null);

            const serialsToRetry = areSerialNumbersSame(serialNumbers, originalSerialNumbers)
                ? faultySerialNumbers   // user didn't change anything, retry faults
                : serialNumbers;         // user changed serials, retry all

            createRequest(serialsToRetry);
        } else {
            setOpenTab(openTab + 1);
        }
    };



    console.log("serialNumbers :", serialNumbers);
    console.log("hasFaultySerials: ", hasFaultySerials);
    console.log("faultySerialNumbers: ", faultySerialNumbers);

    const getHeader = () => {
        return <div className="text-gray-600 text-opacity-50 text-2xl text-center w-full flex justify-center items-center font-semibold py-6">Parts</div>
    };

    return (
        <>
            <DefaultAddFormHeader items={tabItems} openTab={openTab} />
            <form onSubmit={(e) => nextBtnHandler(e)}>
                <div className={openTab === 1 ? "block" : "hidden"} id="link1">
                    <div
                        className="w-full md:w-[39%] ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[30.1rem]">
                        {getHeader()}
                        <div className="relative w-full py-2">
                            <PartsSpecificationsSelect required={openTab === 1} label="Part specifications"
                                onChange={(e) => partSpecificationsHandler(e)} />
                        </div>
                    </div>
                </div>
                <div className={openTab === 2 ? "block" : "hidden"} id="link2">
                    <div
                        className="w-full md:w-[39%] ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[30.1rem]">
                        {getHeader()}
                        <div className="relative w-full py-2">
                            <DefaultTextArea 
                            required={openTab === 2} 
                            label="Serial numbers"
                            value={serialInputText}
                            onChange={(e) => serialNumberHandler(e)} 
                            />
                        </div>
                    </div>
                </div>
                <div className={openTab === 3 ? "block" : "hidden"} id="link3">
                    <div
                        className="w-full md:w-[39%] ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[30.1rem]">
                        {getHeader()}
                        <div className="relative w-full py-2">
                            <div className="relative w-full py-2">
                                <DefaultInput required={openTab === 3} type="text" label="Order number supplier"
                                    onChange={(e) => orderNumberHandler(e)} />
                            </div>
                            <div className="relative w-full py-2">
                                <DefaultInput required={openTab === 3} type="text" label="Supplier"
                                    onChange={(e) => supplierHandler(e)} />
                            </div>
                            {/*<div className="relative w-full py-2">*/}
                            {/*    <DefaultInput required={openTab === 3} type="text" label="Order date"*/}
                            {/*                  onChange={(e) => orderDateHandler(e)}/>*/}
                            {/*</div>*/}
                            <div className="relative w-full py-2">
                                <DefaultDatePicker type="text" label="Shipping date" onChange={(e) => shippingDateHandler(e)} />
                            </div>
                            <div className="relative w-full py-2">
                                <DefaultDatePicker type="text" label="Order date" onChange={(e) => orderDateHandler(e)} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={openTab === lastIndex ? "block" : "hidden"} id="link4">



                    {loading ? (
                        <div className="w-full md:w-[39%] ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[39.1rem]">
                            {getHeader()}
                            <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : error ? (
                        hasFaultySerials ? (
                            <div className="ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[39.1rem] text-center">
                                {getHeader()}
                                <XCircleIcon className="h-32 w-32 text-red-500" aria-hidden="true" />
                                <span className="text-red-500 font-bold text-2xl py-3">Error</span>
                                <span className="text-gray-400 font-semibold text-sm mb-2">
                                    Some serial number(s) already exist. Please choose different serial number(s):
                                </span>
                                <ul className="text-gray-400 font-semibold text-sm list-disc pl-5 text-left">
                                    {faultySerialNumbers.map((serial, index) => (
                                        <li key={index}>{serial}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[39.1rem] text-center">
                                {getHeader()}
                                <XCircleIcon className="h-32 w-32 text-red-500" aria-hidden="true" />
                                <span className="text-red-500 font-bold text-2xl py-3">Error</span>
                                <span className="text-gray-400 font-semibold text-sm">
                                    Could not add pre-assembled Part(s) to inventory.
                                </span>
                                <span className="text-gray-400 font-semibold text-sm">
                                    Please check if fields are filled in correctly or try again later.
                                </span>
                            </div>
                        )
                    ) : (
                        <div className="ml-auto mr-auto px-4 flex flex-col justify-center items-center h-[39.1rem] text-center">
                            {getHeader()}
                            <CheckCircleIcon className="h-32 w-32 text-green-500" aria-hidden="true" />
                            <span className="text-green-500 font-bold text-2xl py-3">Success</span>
                            <span className="font-semibold text-gray-400 text-sm">
                                Successfully added {serialNumbers.length} pre-assembled Part(s)
                            </span>
                            <span className="font-semibold text-gray-400 text-sm">
                                to inventory.
                            </span>
                        </div>
                    )}
                </div>
                <DefaultAddFormNavigation open={openTab}
                    lastIndex={lastIndex}
                    error={error}
                    loading={loading}
                    data={data}
                    previousBtnHandler={() => setOpenTab(openTab - 1)} />
            </form>
        </>
    )
}